import { makeAutoObservable, onBecomeObserved, onBecomeUnobserved } from "mobx";

import { ClassroomStore } from "@/store/ClassroomStore";
import { DocumentTreeStore } from "@/store/DocumentTreeStore";
import { HintsStore } from "@/store/HintsStore";
import { NotificationsStore } from "@/store/NotificationsStore";
import { PublicProfileStore } from "@/store/PublicProfileStore";
import { SearchStore } from "@/store/SearchStore";
import { StudentOnboardingStore } from "@/store/StudentOnboardingStore";
import { TeamsStore } from "@/store/TeamsStore";
import { isLazyStore, isDisposableStore } from "@/store/types";
import { UserSettingsStore } from "@/store/UserSettingsStore";
import { ViewStore } from "@/store/ViewStore";

import { DialogStore } from "./dialog/DialogStore";
import { SignInFlowStore } from "./SignInFlowStore";

import type { AuthModel } from "@/models/auth/AuthModel";
import type { DocumentModel } from "@/models/DocumentModel";
import type { ChatsStore } from "@/store/ChatsStore";
import type { RecentDocumentsStore } from "@/store/RecentDocumentsStore";
import type { TeamChatsStore } from "@/store/TeamChatsStore";
import type { AppRouterInstance } from "next/dist/shared/lib/app-router-context.shared-runtime";

export class RootStore {
  auth: AuthModel;

  teams: TeamsStore;

  documentTree: DocumentTreeStore;

  view: ViewStore;

  userSettings: UserSettingsStore;

  router: AppRouterInstance;

  doc?: DocumentModel;

  dialog: DialogStore;

  chats?: ChatsStore = undefined;

  teamChats?: TeamChatsStore = undefined;

  recentDocuments?: RecentDocumentsStore = undefined;

  publicProfile: PublicProfileStore;

  search: SearchStore;

  hints: HintsStore;

  signInFlow: SignInFlowStore;

  notifications: NotificationsStore;

  classroom: ClassroomStore;

  studentOnboarding: StudentOnboardingStore;

  constructor({ auth, router }: { auth: AuthModel; router: AppRouterInstance }) {
    this.router = router;
    this.auth = auth;
    auth.rootStore = this;

    this.view = new ViewStore(this);
    this.teams = new TeamsStore(this);
    this.documentTree = new DocumentTreeStore(this);
    this.userSettings = new UserSettingsStore(this);
    this.dialog = new DialogStore(this);
    this.publicProfile = new PublicProfileStore(this);
    this.search = new SearchStore(this);
    this.hints = new HintsStore(this);
    this.signInFlow = new SignInFlowStore(this);
    this.notifications = new NotificationsStore(this);
    this.classroom = new ClassroomStore(this);
    this.studentOnboarding = new StudentOnboardingStore(this);

    makeAutoObservable(this, {
      doc: true,
    });

    this.autoDisposeAndEnableLazyStores();
  }

  private autoDisposeAndEnableLazyStores() {
    for (const p of Object.keys(this)) {
      const storeName = p as keyof this;
      const store = this[storeName];

      if (isLazyStore(store)) {
        onBecomeObserved(this, storeName, () => {
          store.lazyEnable();
        });
      }
      if (isDisposableStore(store)) {
        onBecomeUnobserved(this, storeName, () => {
          store.dispose();
        });
      }
    }
  }

  dispose() {
    this.chats?.dispose();
    this.chats = undefined;

    this.teamChats?.dispose();
    this.teamChats = undefined;

    this.recentDocuments?.dispose();
    this.recentDocuments = undefined;
  }
}
